import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

const SfaTable = ({ sfas, onPageChange, currentPage }) => {
    if (!sfas || !sfas.data) return null;

    const getStatusBadge = (sfa) => {
        if (!sfa.complete) {
            return <span className="badge badge-warning">Incomplete</span>;
        }
        
        if (!sfa.sfa_review) {
            return <span className="badge badge-info">Pending Review</span>;
        }
        
        const status = sfa.sfa_review.status;
        const statusColors = {
            'in_review': 'badge-secondary',
            'approved': 'badge-success',
            'rejected': 'badge-danger'
        };
        
        return (
            <span className={`badge ${statusColors[status] || 'badge-secondary'}`}>
                {status ? status.replace('_', ' ').toUpperCase() : 'Unknown'}
            </span>
        );
    };

    const getCompletionProgress = (percentComplete) => {
        const percentage = percentComplete || 0;
        const color = percentage >= 100 ? 'success' : percentage >= 75 ? 'info' : percentage >= 50 ? 'warning' : 'danger';
        
        return (
            <div className="progress" style={{ height: '20px' }}>
                <div 
                    className={`progress-bar bg-${color}`} 
                    style={{ width: `${percentage}%` }}
                    role="progressbar"
                >
                    {percentage}%
                </div>
            </div>
        );
    };

    const formatDate = (dateString) => {
        return moment(dateString).format('MMM DD, YYYY');
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const renderPagination = () => {
        if (!sfas.last_page || sfas.last_page <= 1) return null;

        const pages = [];
        const startPage = Math.max(1, currentPage - 2);
        const endPage = Math.min(sfas.last_page, currentPage + 2);

        // Previous button
        if (currentPage > 1) {
            pages.push(
                <li key="prev" className="page-item">
                    <button 
                        className="page-link" 
                        onClick={() => onPageChange(currentPage - 1)}
                    >
                        Previous
                    </button>
                </li>
            );
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
                    <button 
                        className="page-link" 
                        onClick={() => onPageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        // Next button
        if (currentPage < sfas.last_page) {
            pages.push(
                <li key="next" className="page-item">
                    <button 
                        className="page-link" 
                        onClick={() => onPageChange(currentPage + 1)}
                    >
                        Next
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="SFA pagination">
                <ul className="pagination justify-content-center">
                    {pages}
                </ul>
            </nav>
        );
    };

    return (
        <div className="table-responsive">
            <table className="table table-striped table-hover">
                <thead className="thead-dark">
                    <tr>
                        <th>ID</th>
                        <th>Student</th>
                        <th>School</th>
                        <th>Status</th>
                        <th>Completion</th>
                        <th>Amount Approved</th>
                        <th>Created</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sfas.data.map((sfa) => (
                        <tr key={sfa.id}>
                            <td>
                                <strong>#{sfa.id}</strong>
                            </td>
                            <td>
                                <div>
                                    <strong>
                                        {sfa.sfa_student_info?.first_name} {sfa.sfa_student_info?.last_name}
                                    </strong>
                                    <br />
                                    <small className="text-muted">
                                        {sfa.user?.email}
                                    </small>
                                </div>
                            </td>
                            <td>
                                {sfa.sfa_school_info?.school_name || '-'}
                            </td>
                            <td>
                                {getStatusBadge(sfa)}
                            </td>
                            <td style={{ width: '150px' }}>
                                {getCompletionProgress(sfa.percent_complete)}
                            </td>
                            <td>
                                {sfa.sfa_review?.amount_approved ? 
                                    formatCurrency(sfa.sfa_review.amount_approved) : 
                                    '-'
                                }
                            </td>
                            <td>
                                {formatDate(sfa.created_at)}
                            </td>
                            <td>
                                <div className="btn-group" role="group">
                                    <Link 
                                        to={`/sfa/${sfa.id}`} 
                                        className="btn btn-sm btn-outline-primary"
                                        title="View/Edit SFA"
                                    >
                                        <i className="fas fa-eye"></i>
                                    </Link>
                                    
                                    {sfa.complete && !sfa.sfa_review && (
                                        <Link 
                                            to={`/sfa-review/${sfa.id}`} 
                                            className="btn btn-sm btn-outline-success"
                                            title="Review SFA"
                                        >
                                            <i className="fas fa-search"></i>
                                        </Link>
                                    )}
                                    
                                    {sfa.sfa_review && (
                                        <Link 
                                            to={`/sfa-review/${sfa.id}`} 
                                            className="btn btn-sm btn-outline-info"
                                            title="View Review"
                                        >
                                            <i className="fas fa-clipboard-check"></i>
                                        </Link>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination Info */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                    Showing {((currentPage - 1) * sfas.per_page) + 1} to {Math.min(currentPage * sfas.per_page, sfas.total)} of {sfas.total} entries
                </div>
                {renderPagination()}
            </div>

            {/* Empty State */}
            {sfas.data.length === 0 && (
                <div className="text-center py-5">
                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No SFA applications found</h5>
                    <p className="text-muted">Try adjusting your filters or create a new SFA application.</p>
                    <Link to="/sfa" className="btn btn-primary">
                        <i className="fas fa-plus mr-1"></i>
                        Create New SFA
                    </Link>
                </div>
            )}
        </div>
    );
};

export default SfaTable; 